import { type KeyboardEvent, type MouseEvent, useCallback, useState } from "react";

import { SelectField, type SelectFieldProps } from "#components/v2/SelectField";
import { useUIVersion } from "#hooks";
import { ClearIcon } from "#icons";
import { cn, customTv } from "#utils";

/** Represents a single item in the MultiSelectField */
export interface MultiSelectFieldItem {
  /** Unique identifier for the item */
  id: string;
  /** Display name for the item */
  name: string;
}

const tagStyles = customTv({
  base: "inline-flex items-center justify-center gap-x-xs px-xs text-text-primary",
  variants: {
    isDisabled: {
      true: "opacity-50",
    },
    isReadOnly: {
      true: "!text-label-small",
    },
    uiVersion: {
      v1: "rounded-1 border border-legacy-neutrals-denseFog bg-transparent font-semibold text-label-small px-[12px] py-[5px]",
      v2: "rounded-md bg-data-viz-soft-indigo text-label-extra-small-strong",
    },
  },
});

const removeButtonStyles = customTv({
  base: "inline-flex cursor-pointer items-center hover:text-outline-input-hover focus-visible:text-outline-input-focus focus-visible:outline-none",
  variants: {
    uiVersion: {
      v1: "p-3xs rounded-full bg-legacy-navy-base opacity-25 text-neutral-white hover:text-neutral-white hover:opacity-40",
      v2: "",
    },
  },
});

/** MultiSelectFieldProps interface for the MultiSelectField component */
export interface MultiSelectFieldProps extends Omit<SelectFieldProps, "value"> {
  /** Array of selected items (controlled mode) */
  value?: MultiSelectFieldItem[];
  /** Default selected items (uncontrolled mode) */
  defaultValue?: MultiSelectFieldItem[];
  /** Callback when items change (works in both controlled and uncontrolled modes) */
  onChange?: (items: MultiSelectFieldItem[]) => void;
  /** When true, shows an X button next to the chevron to clear all tags (only when items exist and not disabled/readonly) */
  showClearAll?: boolean;
}

/**
 * MultiSelectField component - A trigger element styled like SelectField
 * that displays selected values as removable Tags.
 *
 * Supports both controlled (value + onChange) and uncontrolled (defaultValue + onChange) modes.
 * Selected items are displayed as small soft-indigo tags (styled to match V2 Tag)
 * with a clickable remove icon.
 */
export function MultiSelectField(props: MultiSelectFieldProps) {
  const {
    value: controlledValue,
    defaultValue,
    onChange,
    isDisabled,
    isReadOnly,
    showClearAll = false,
    ...restProps
  } = props;

  const uiVersion = useUIVersion();
  const isControlled = controlledValue !== undefined;

  const [internalValue, setInternalValue] = useState<MultiSelectFieldItem[]>(defaultValue ?? []);

  const items = isControlled ? controlledValue : internalValue;

  const removeItem = useCallback(
    (id: string) => {
      const updatedItems = items.filter((item) => item.id !== id);

      if (!isControlled) {
        setInternalValue(updatedItems);
      }

      onChange?.(updatedItems);
    },
    [items, isControlled, onChange],
  );

  const clearAll = useCallback(() => {
    if (!isControlled) {
      setInternalValue([]);
    }
    onChange?.([]);
  }, [isControlled, onChange]);

  const hasItems = items.length > 0;
  const canRemove = !isDisabled && !isReadOnly;
  const showClearAllButton = showClearAll && hasItems && canRemove;

  const renderedValue = hasItems ? (
    <span
      className="flex min-h-5 flex-wrap items-center gap-xs py-[0.0625rem] text-text-primary"
      role="list"
    >
      {items.map((item) => (
        <span
          className={tagStyles({ isDisabled, isReadOnly, uiVersion })}
          key={item.id}
          role="listitem"
        >
          {item.name}
          {canRemove ? (
            <span
              aria-label={`Remove ${item.name}`}
              className={removeButtonStyles({ uiVersion })}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();

                removeItem(item.id);
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();

                  removeItem(item.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <ClearIcon size={14} />
            </span>
          ) : null}
        </span>
      ))}
    </span>
  ) : undefined;

  const clearAllAdornment = showClearAllButton ? (
    <span
      aria-label="Clear all"
      className={cn(removeButtonStyles({ uiVersion: "v2" }), "ml-xs shrink-0")}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        clearAll();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.stopPropagation();
          e.preventDefault();
          clearAll();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <ClearIcon size={14} />
    </span>
  ) : undefined;

  return (
    <SelectField
      {...restProps}
      buttonClassName={cn(uiVersion === "v1" ? "p-[6px]" : "")}
      endAdornment={clearAllAdornment}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      value={renderedValue}
    />
  );
}
